import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    MenuItem,
    Typography,
    Autocomplete,
    Chip
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { useEffect } from "react";
import { get, post, deleteEntry } from "src/services/httpClient.service";
import { authenticationService } from "src/services/authentication.service";
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import UpdateMediaServer from "./UpdateMediaServer";
import CircularProgress from "@mui/material/CircularProgress";

interface District {
    id: number;
    name: string;
}

interface City {
    id: number;
    city: string;
}



const MediaServer = () => {
    const { showSnackbar } = useSnackbar();
    const { vendorId } = authenticationService.getVendorFranchiseIds();
    const API_URL = process.env.REACT_APP_BASE_API_URL;
    const [districts, setDistricts] = useState<District[]>([]);
    const [cities, setCities] = useState<any[]>([]);
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    // const [loadingCities, setLoadingCities] = useState(false);
    // const [selectedCityInput, setSelectedCityInput] = useState('');
    // const [selectedCityId, setSelectedCityId] = useState('');
    // const [selectedCityName, setSelectedCityName] = useState('');
    // const [selectedDistrictId, setSelectedDistrictId] = useState('');
    // const [selectedDistrictName, setSelectedDistrictName] = useState('');

    // const fetchDistricts = async () => {
    //     try {
    //         const response = await get(
    //             `${API_URL}/api/miscellaneous/district/vendor/${vendorId}/findAll`
    //         );

    //         if (response) {
    //             setDistricts(response.data || []);
    //         }
    //     } catch (error) {
    //         console.log(error);
    //     }
    // };

    const fetchCities = async () => {
        try {
            const response = await get(`${API_URL}/api/miscellaneous/city/vendor/${vendorId}/findAll`);

            if (response) {
                setCities(response.data || []);
            }
        } catch (error) {
            console.log(error);
        }
    };


    useEffect(() => {
        //fetchDistricts();
        fetchCities();
        fetchMediaServers();
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        url: '',
        status: 'ACTIVE',
        districtId: '',
        districtName: '',
        cityId: '',
        cityName: '',
        stateId: '',
        stateName: '',
        serverUserName: '',
        serverPassword: '',
        vendorId: vendorId,
    });

    const [rows, setRows] = useState<any[]>([]);



    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
        },
        {
            field: 'url',
            headerName: 'URL',
            flex: 1.5,
        },
        // {
        //     field: 'serverUserName',
        //     headerName: 'Username',
        //     flex: 1,
        // },
        {
            field: 'districtName',
            headerName: 'District',
            flex: 1,
        },
        {
            field: 'cityName',
            headerName: 'City',
            flex: 1,
        },
        {
            field: 'stateName',
            headerName: 'State',
            flex: 1,
        },
        {
            field: 'urlStatus',
            headerName: 'Status',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const isRunning = params.value === "ONLINE" || params.value === "online";

                return (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%"
                        }}
                    >
                        <Chip
                            label={isRunning ? "ONLINE" : "OFFLINE"}
                            size="small"
                            sx={{
                                backgroundColor: isRunning ? "#64dd17" : "#ff2d55",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "12px",
                                minWidth: 75,
                                borderRadius: "16px",
                                "& .MuiChip-label": {
                                    px: 1.5
                                }
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            field: "action",
            headerName: "Action",
            width: 140,
            sortable: false,
            renderCell: (params) => (
                <>
                    <IconButton
                        color="primary"
                        onClick={() => handleEdit(params.row)}
                    >
                        <EditIcon />
                    </IconButton>

                    <IconButton
                        color="error"
                        onClick={() => {
                            setSelectedDeleteId(params.row.id);
                            setDeleteDialogOpen(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </>
            )
        }
    ];

    const fetchMediaServers = async () => {
        setLoading(true);

        try {
            const response = await get(`${API_URL}/api/vendor-media-servers/vendor/${vendorId}/findAll`);

            if (response) {
                setRows(response?.data || []);
            }
        } catch (error: any) {
            showSnackbar(
                error?.message || "Failed to fetch Media Servers",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSave = async () => {
        setSaveLoading(true);

        try {
            const payload = {
                name: formData.name?.trim(),
                url: formData.url?.trim(),
                cityId: formData.cityId,
                cityName: formData.cityName?.trim(),
                districtId: formData.districtId,
                districtName: formData.districtName?.trim(),
                stateId: formData.stateId,
                stateName: formData.stateName?.trim(),
                serverPassword: formData.serverPassword,
                serverUserName: formData.serverUserName,
                status: formData.status,
                vendorId: vendorId
            };

            const response = await post(`${API_URL}/api/vendor-media-servers`, payload);

            showSnackbar(
                response?.message || "Media Server added successfully",
                "success"
            );

            setOpen(false);

            setFormData({
                name: "",
                url: "",
                status: "ACTIVE",
                districtId: "",
                districtName: "",
                cityId: "",
                cityName: "",
                stateId: "",
                stateName: "",
                serverUserName: "",
                serverPassword: "",
                vendorId: vendorId,
            });

            fetchMediaServers();
        } catch (error: any) {
            console.log('error',error)
            showSnackbar(
                error?.response?.data ||
                error?.message ||
                "Failed to save media Server",
                "error"
            );
        } finally {
            setSaveLoading(false);
        }
    };

    const handleEdit = (row: any) => {
        setSelectedRow(row);
        setEditOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedDeleteId) return;

        setDeleteLoading(true);

        try {
            const response = await deleteEntry(
                `${API_URL}/api/vendor-media-servers/${selectedDeleteId}`
            );

            showSnackbar(
                response?.message || "Media Server deleted successfully",
                "success"
            );

            fetchMediaServers();
        } catch (error: any) {
            showSnackbar(
                error?.message || "Delete Failed",
                "error"
            );
        } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
            setSelectedDeleteId(null);
        }
    };

    return (
        <Box p={3}>

            <UpdateMediaServer
                open={editOpen}
                onClose={() => setEditOpen(false)}
                data={selectedRow}
                districts={districts}
                cities={cities}
                onSuccess={fetchMediaServers}
            />
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h5">
                    Media Servers
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpen(true)}
                >
                    Add Media Server
                </Button>
            </Box>

            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                loading={loading}
                pageSize={10}
                rowsPerPageOptions={[10]}
                disableSelectionOnClick
            />

            {/* Add Dialog */}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Add Media Server
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Server Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Server URL"
                                name="url"
                                value={formData.url}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Server Username"
                                name="serverUserName"
                                value={formData.serverUserName}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="password"
                                label="Server Password"
                                name="serverPassword"
                                value={formData.serverPassword}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <MenuItem value="ACTIVE">
                                    ACTIVE
                                </MenuItem>

                                <MenuItem value="INACTIVE">
                                    INACTIVE
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box>
                                <Autocomplete
    options={cities}
    value={
        cities.find(c => String(c.id) === String(formData.cityId)) || null
    }
    getOptionLabel={(option) =>
        `${option.city} / ${option.districtName} / ${option.stateName} / ${option.countryName} / ${option.zipCode}`
    }
    isOptionEqualToValue={(option, value) =>
        option.id === value.id
    }
    onChange={(_, value) => {
        if (!value) {
            setFormData(prev => ({
                ...prev,
                cityId: "",
                cityName: "",
                districtId: "",
                districtName: "",
                stateId: "",
                stateName: ""
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            cityId: value.id,
            cityName: value.city,
            districtId: value.districtId,
            districtName: value.districtName,
            stateId: value.stateId,
            stateName: value.stateName
        }));
    }}
    renderInput={(params) => (
        <TextField {...params} label="City" />
    )}
/>
                                
                                </Box>  
                        </Grid>
                        {/* <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="District"
                                name="districtId"
                                value={formData.districtId}
                                onChange={handleChange}
                            >
                                {districts.map((district) => (
                                    <MenuItem
                                        key={district.id}
                                        value={district.id}
                                    >
                                        {district.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="City"
                                name="cityId"
                                value={formData.cityId}
                                onChange={handleChange}
                            >
                                {cities.map((city) => (
                                    <MenuItem
                                        key={city.id}
                                        value={city.id}
                                    >
                                        {city.city}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid> */}


                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setOpen(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saveLoading}
                        startIcon={
                            saveLoading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : null
                        }
                    >
                        {saveLoading ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Media Server</DialogTitle>

                <DialogContent>
                    Are you sure you want to delete this Media Server?
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setSelectedDeleteId(null);
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        startIcon={
                            deleteLoading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : null
                        }
                    >
                        {deleteLoading ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MediaServer;